import React, { useState, useRef, useEffect } from "react";
import { MapPinIcon } from "@heroicons/react/24/outline";
import { Transition } from "@headlessui/react";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import AnimatedFlow from "../AnimatedFlow/AnimatedFlow";
import {
  createSecondaryCalendar,
  getSecondaryCalendarId,
  createEvent,
} from "../../gcal";

const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
];
const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB

// Google Calendar API configuration
const GOOGLE_CLIENT_ID =
  "596912846116-9g3i9kn9rhgi9d7sk5kqkb46kbhgabe5.apps.googleusercontent.com";
const GOOGLE_SCOPES =
  "https://www.googleapis.com/auth/calendar.calendarlist.readonly https://www.googleapis.com/auth/calendar.app.created https://www.googleapis.com/auth/userinfo.email";
const GOOGLE_REDIRECT_URI = window.location.origin;

function ImageUpload() {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [eventsAddedSuccessfully, setEventsAddedSuccessfully] = useState(false);
  const [googleAccessToken, setGoogleAccessToken] = useState(null);
  const [googleEmailAddress, setGoogleEmailAddress] = useState("");
  const fileInputRef = useRef(null);

  const analyzeImageWithAI = async (file) => {
    try {
      setIsProcessing(true);
      const workerUrl = "https://calplease-api.sven.workers.dev/";

      const response = await fetch(workerUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: file,
      });

      if (!response.ok) {
        throw new Error(`Worker request failed: ${response.status}`);
      }

      const data = await response.json();
      console.log(data);

      if (!data.success) {
        throw new Error(data.error || "Failed to analyze image");
      }

      if (data.events.length === 0) {
        setError("No events were found in the image");
      }

      const events = data.events.map((ev) => ({
        title: ev.title,
        location: ev.location,
        date: `${ev.year}-${ev.month}-${ev.day}`,
        //`
        startTime: ev.start_time,
        endTime: ev.end_time,
        description: ev.description,
      }));
      setEvents(events);

      console.log(events);
    } catch (error) {
      setError("Failed to process image");
    } finally {
      setIsProcessing(false);
    }
  };
  // Validate and process image file
  const processImage = async (file) => {
    if (!file) return;

    // Clear previous error
    setError(null);

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(
        "Please upload a valid image file (JPEG, PNG, GIF, WebP, or SVG)",
      );
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError(`File size must be less than ${MAX_FILE_SIZE / 1024 / 1024}MB`);
      return;
    }

    // Clean up previous image URL if exists
    if (uploadedImage?.url) {
      URL.revokeObjectURL(uploadedImage.url);
    }

    // Create object URL for the new image
    const imageUrl = URL.createObjectURL(file);
    setUploadedImage({
      url: imageUrl,
      name: file.name,
      size: file.size,
      type: file.type,
    });

    await analyzeImageWithAI(file);
  };

  // Handle file selection from file picker
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    processImage(file);
  };

  // Handle upload button click
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Handle clear button
  const handleClearImage = () => {
    if (uploadedImage?.url) {
      URL.revokeObjectURL(uploadedImage.url);
    }
    setUploadedImage(null);
    setError(null);
    setEvents([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle Google Calendar connection
  const handleConnectGoogleCalendar = () => {
    // Save current state to localStorage before OAuth redirect
    const stateToSave = {
      events,
      imageName: uploadedImage?.name,
      imageUrl: uploadedImage?.url,
    };
    localStorage.setItem("calplease_oauth_state", JSON.stringify(stateToSave));

    const authUrl =
      `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${GOOGLE_CLIENT_ID}&` +
      `redirect_uri=${encodeURIComponent(GOOGLE_REDIRECT_URI)}&` +
      `response_type=token&` +
      `scope=${encodeURIComponent(GOOGLE_SCOPES)}`;

    window.location.href = authUrl;
  };

  // Function to add events to Google Calendar
  const addEventsToGoogleCalendar = async (eventsToAdd) => {
    if (!googleAccessToken || !eventsToAdd || eventsToAdd.length === 0) return;

    try {
      let calendarId = await getSecondaryCalendarId(googleAccessToken);
      if (calendarId === null) {
        calendarId = await createSecondaryCalendar(googleAccessToken);
        console.debug("created secondary calendar", { calendarId });
      } else {
        console.debug("existing secondary calendar", { calendarId });
      }

      for (const event of eventsToAdd) {
        // Convert event to Google Calendar format
        const startDateTime = `${event.date}T${event.startTime}:00`;
        const endDateTime = `${event.date}T${event.endTime}:00`;

        const googleEvent = {
          summary: event.title,
          description: event.description || "",
          location: event.location || "",
          start: {
            dateTime: startDateTime,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          end: {
            dateTime: endDateTime,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        };

        await createEvent(googleAccessToken, calendarId, googleEvent);
      }
    } catch (err) {
      console.warn(err);
      setError(err.message);
      setEventsAddedSuccessfully(false);
      return;
    }

    setEventsAddedSuccessfully(true);
  };

  // Handle adding events to Google Calendar
  const handleAddToGoogleCalendar = async () => {
    // If not connected, trigger connection flow first
    if (!isGoogleConnected) {
      handleConnectGoogleCalendar();
      return;
    }

    if (!googleAccessToken || events.length === 0) return;
    await addEventsToGoogleCalendar(events);
  };

  // Handle paste event for clipboard images
  useEffect(() => {
    const handlePaste = (event) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf("image") !== -1) {
          event.preventDefault();
          const file = items[i].getAsFile();
          processImage(file);
          break;
        }
      }
    };

    window.addEventListener("paste", handlePaste);

    return () => {
      window.removeEventListener("paste", handlePaste);
    };
    // eslint-disable-next-line
  }, [uploadedImage]);

  // Cleanup object URL on unmount or when image changes
  useEffect(() => {
    return () => {
      if (uploadedImage?.url) {
        URL.revokeObjectURL(uploadedImage.url);
      }
    };
  }, [uploadedImage]);

  // Handle OAuth callback from Google
  useEffect(() => {
    const storedOauthToken = localStorage.getItem("oauth_token");

    if (storedOauthToken) {
      setIsProcessing(true);
      fetch(
        "https://www.googleapis.com/oauth2/v3/userinfo?access_token=" +
          storedOauthToken,
      )
        .then((res) => {
          if (res.status !== 200) {
            throw new Error("returned HTTP code " + res.status);
          } else {
            return res.json();
          }
        })
        .then((profile) => {
          console.log(profile);
          setGoogleEmailAddress(profile.email);
          setGoogleAccessToken(storedOauthToken);
          setIsGoogleConnected(true);
        })
        .catch((err) => {
          console.warn("failed to get user profile", err);
          setGoogleAccessToken(null);
          setIsGoogleConnected(false);
          setGoogleEmailAddress("");

          localStorage.removeItem("oauth_token");
        })
        .finally(() => {
          setIsProcessing(false);
        });
    }

    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get("access_token");

      if (accessToken) {
        setIsProcessing(true);

        fetch(
          "https://www.googleapis.com/oauth2/v3/userinfo?access_token=" +
            accessToken,
        )
          .then((res) => {
            if (res.status !== 200) {
              setError("Failed to connect to Google");
              throw new Error("returned HTTP code " + res.status);
            } else {
              return res.json();
            }
          })
          .then((profile) => {
            console.log(profile);
            setGoogleEmailAddress(profile.email);
            setGoogleAccessToken(accessToken);
            setIsGoogleConnected(true);
            localStorage.setItem("oauth_token", accessToken);

            window.history.replaceState(null, "", window.location.pathname);
          })
          .catch((err) => {
            console.warn("failed to get user profile", err);
            setGoogleAccessToken(null);
            setIsGoogleConnected(false);
            setGoogleEmailAddress("");
            localStorage.removeItem("oauth_token");
          })
          .finally(() => {
            setIsProcessing(false);
          });
      }
    }
  }, []);

  return (
    <div
      className={`w-full mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ease-in-out ${uploadedImage ? "pb-2" : "py-5"}`}
    >
      <Error show={error !== null} close={() => setError(null)} text={error} />
      <Success
        show={eventsAddedSuccessfully}
        setShow={setEventsAddedSuccessfully}
        text={`Added ${events.length} event(s) to Google Calendar!`}
      />
      <div
        className={`text-center px-4 transition-all duration-700 ease-in-out ${uploadedImage ? "mb-0" : "mb-8 sm:mb-12"}`}
      >
        {!uploadedImage && (
          <div className="mb-8 transition-all duration-500 ease-in-out animate-fade-in">
            <AnimatedFlow />
          </div>
        )}

        {!uploadedImage && (
          <>
            <h1
              className={`font-bold text-gray-900 tracking-tight bg-gradient-to-br from-blue-500 to-purple-600 bg-clip-text text-transparent transition-all duration-500 text-3xl sm:text-4xl lg:text-5xl mb-4`}
            >
              calplease.com
            </h1>
            <p className="text-base sm:text-lg text-gray-600 mx-auto leading-relaxed font-normal transition-all duration-500">
              Extract calendar events from images using AI. <br />
              Upload photos of chat conversations, sports bookings, wedding invitations, schedules, or any event-containing images and automatically add them to Google Calendar.
              <br />
              <span className="text-sm text-gray-500 mt-2 inline-block">
                iCloud Calendar and Proton Calendar integration coming soon.
              </span>
            </p>
          </>
        )}

        <div
          className={`flex flex-col items-center gap-2 transition-all duration-500 mt-8`}
        >
          {!isGoogleConnected ? (
            <button
              className={`bg-[#4285f4] text-white border-none font-medium rounded-lg cursor-pointer transition-all shadow-sm flex items-center gap-2.5 hover:bg-[#3367d6] hover:shadow-md active:bg-[#2851a3] focus:outline-none focus:shadow-[0_0_0_3px_rgba(66,133,244,0.3)] px-6 py-3 text-[0.95rem]`}
              onClick={handleConnectGoogleCalendar}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36363H9V10.845H13.8436C13.635 11.97 13.0009 12.9231 12.0477 13.5613V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.20454Z"
                  fill="#4285F4"
                />
                <path
                  d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z"
                  fill="#34A853"
                />
                <path
                  d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.82999 3.96409 7.28999V4.95818H0.957275C0.347727 6.17318 0 7.54772 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z"
                  fill="#FBBC05"
                />
                <path
                  d="M9 3.57954C10.3214 3.57954 11.5077 4.03363 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01681 0.957275 4.95818L3.96409 7.28999C4.67182 5.16272 6.65591 3.57954 9 3.57954Z"
                  fill="#EA4335"
                />
              </svg>
              Connect to Google Calendar
            </button>
          ) : (
            <div
              className={`flex flex-col sm:flex-row items-center gap-2 text-green-600 font-medium bg-green-50 rounded-lg text-center transition-all duration-500 text-sm sm:text-[0.95rem] px-4 py-3`}
            >
              <div className="flex items-center gap-2">
                <svg
                  width={"18"}
                  height={"18"}
                  viewBox="0 0 18 18"
                  fill="none"
                  className="shrink-0"
                >
                  <circle cx="9" cy="9" r="8" fill="#34A853" />
                  <path
                    d="M6 9L8 11L12 7"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Connected to Google Calendar</span>
              </div>
              <span className="text-xs sm:text-sm text-gray-600">
                ({googleEmailAddress})
              </span>
            </div>
          )}
        </div>
      </div>

      <div
        className={`text-center bg-white rounded-xl shadow-sm border border-gray-200 transition-all duration-700 ease-in-out mb-6 sm:mb-10 py-8 sm:py-12 px-4 sm:px-10 mt-6`}
      >
        {uploadedImage ? (
          <>
            <div className="w-full flex justify-center items-center bg-white rounded-lg p-2 min-h-[150px] transition-all duration-500">
              <img
                src={uploadedImage.url}
                alt={uploadedImage.name}
                className="max-w-full max-h-[350px] h-auto rounded-lg shadow-lg object-contain transition-all duration-500"
              />
            </div>
            <button
              onClick={handleClearImage}
              className="mt-2 bg-red-600 text-white border-none px-4 py-1.5 text-xs font-medium rounded-lg cursor-pointer transition-all shadow-sm hover:bg-red-700 hover:shadow-md active:bg-red-800 focus:outline-none focus:shadow-[0_0_0_3px_rgba(229,62,62,0.3)]"
            >
              Clear Image
            </button>
          </>
        ) : (
          <>
            <h2 className="mb-6 text-gray-900 text-2xl sm:text-3xl font-semibold tracking-tight">
              Image Upload
            </h2>

            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              ref={fileInputRef}
              className="hidden"
              aria-label="Upload image file"
            />

            <button
              onClick={handleUploadClick}
              className="bg-main text-white border-none px-8 py-3.5 text-base font-medium rounded-lg cursor-pointer transition-all mb-5 shadow-sm hover:shadow-md focus:outline-none focus:shadow-[0_0_0_3px_rgba(66,153,225,0.3)] disabled:bg-gray-300 disabled:cursor-not-allowed disabled:text-gray-500"
              disabled={isProcessing}
            >
              {isProcessing ? "Processing with AI..." : "Choose Image"}
            </button>

            {isProcessing && (
              <div className="flex flex-col items-center gap-3 mt-5 p-5 bg-gray-100 rounded-lg border border-gray-200">
                <div className="w-8 h-8 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                <p className="m-0 text-gray-700 text-[0.95rem] font-medium">
                  Analyzing image...
                </p>
              </div>
            )}

            <div className="text-gray-600 mt-6">
              <p className="mb-3 text-[0.95rem] font-medium">
                Upload an image using:
              </p>
              <ul className="list-none p-0 m-0">
                <li className="my-2 text-sm text-gray-500">
                  Click "Choose Image" to select a file
                </li>
                <li className="my-2 text-sm text-gray-500">
                  Press Ctrl+V (Cmd+V on Mac) to paste from clipboard
                </li>
              </ul>
            </div>
          </>
        )}
      </div>

      {uploadedImage && isProcessing && (
        <div className="grid grid-cols-1 gap-3 w-full mt-3 items-start justify-center transition-all duration-500">
          <div className="flex justify-center items-start relative flex-col bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200 w-full transition-all duration-500">
            Making event(s)...
          </div>
        </div>
      )}

      {!isProcessing && events.length > 0 && (
        <div className="grid grid-cols-1 gap-3 w-full mt-3 items-start justify-center transition-all duration-500 animate-fade-in">
          <div className="flex justify-center items-start relative flex-col bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200 w-full transition-all duration-500">
            <h2 className="mb-3 text-gray-900 text-lg sm:text-xl font-medium tracking-tight">
              Events
            </h2>
            <div className="w-full mb-3">
              {events.map((ev) => (
                <div
                  key={ev.title}
                  className="bg-white rounded-lg p-0 mb-2 border border-gray-300 transition-all cursor-pointer flex overflow-hidden shadow-sm hover:shadow-md"
                >
                  <div className="w-1 bg-main shrink-0"></div>
                  <div className="text-black flex-1 p-2.5">
                    <div className="text-base font-medium text-gray-900 mb-2 leading-snug">
                      {ev.title}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <div className="text-sm flex items-center gap-2">
                        <svg
                          className="shrink-0"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M8 14.5C4.41 14.5 1.5 11.59 1.5 8C1.5 4.41 4.41 1.5 8 1.5C11.59 1.5 14.5 4.41 14.5 8C14.5 11.59 11.59 14.5 8 14.5Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                          />
                          <path
                            d="M8 4.5V8L10.5 10.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                        {ev.startTime} - {ev.endTime}
                      </div>
                      <div className="text-sm flex items-center gap-2">
                        <svg
                          className="shrink-0"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <rect
                            x="2.5"
                            y="3.5"
                            width="11"
                            height="10"
                            rx="1.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                          />
                          <path
                            d="M2.5 6.5H13.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                          />
                          <path
                            d="M5.5 2V5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <path
                            d="M10.5 2V5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                        {new Date(ev.date).toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </div>

                      {ev.location && (
                        <div className="text-sm flex items-center gap-2">
                          <MapPinIcon className="h-4 w-6 text-gray-500" />
                          {ev.location}
                        </div>
                      )}
                    </div>
                    {ev.description && (
                      <div className="mt-3 pt-3 border-t border-gray-200 text-sm leading-relaxed">
                        <pre>{ev.description}</pre>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 mt-3 flex-wrap justify-center w-full">
              <button
                className="bg-main text-white border-none px-4 py-2 text-sm font-medium rounded-lg cursor-pointer transition-all shadow-sm flex-1 min-w-[140px] max-w-[240px] hover:shadow-md focus:outline-none focus:shadow-[0_0_0_3px_rgba(66,133,244,0.3)] disabled:bg-gray-300 disabled:cursor-not-allowed disabled:text-gray-500"
                onClick={handleAddToGoogleCalendar}
                disabled={isProcessing || events.length === 0}
              >
                {isProcessing ? "Adding events..." : "Add to Google Calendar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Success({ show, setShow, text }) {
  // Auto-dismiss after 5 seconds
  React.useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        setShow(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [show, setShow]);

  return (
    <>
      <div
        aria-live="assertive"
        className="pointer-events-none fixed inset-0 z-50 flex items-end px-4 py-6 sm:items-start sm:p-6"
      >
        <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
          <Transition show={show}>
            <div className="pointer-events-auto w-full max-w-sm rounded-lg bg-white shadow-lg outline-1 outline-black/5 transition data-closed:opacity-0 data-enter:transform data-enter:duration-300 data-enter:ease-out data-closed:data-enter:translate-y-2 data-leave:duration-100 data-leave:ease-in data-closed:data-enter:sm:translate-x-2 data-closed:data-enter:sm:translate-y-0 dark:bg-gray-800 dark:-outline-offset-1 dark:outline-white/10">
              <div className="p-4">
                <div className="flex items-start">
                  <div className="shrink-0">
                    <CheckCircleIcon
                      aria-hidden="true"
                      className="size-6 text-green-400"
                    />
                  </div>
                  <div className="ml-3 w-0 flex-1 pt-0.5">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Success!
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {text}
                    </p>
                  </div>
                  <div className="ml-4 flex shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        setShow(false);
                      }}
                      className="inline-flex rounded-md text-gray-400 hover:text-gray-500 focus:outline-2 focus:outline-offset-2 focus:outline-indigo-600 dark:hover:text-white dark:focus:outline-indigo-500"
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon aria-hidden="true" className="size-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </>
  );
}

function Error({ show, close, text }) {
  // Auto-dismiss after 5 seconds
  React.useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        close();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [show, close]);

  return (
    <>
      <div
        aria-live="assertive"
        className="pointer-events-none fixed inset-0 z-50 flex items-end px-4 py-6 sm:items-start sm:p-6"
      >
        <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
          <Transition show={show}>
            <div className="pointer-events-auto w-full max-w-sm rounded-lg bg-white shadow-lg outline-1 outline-black/5 transition data-closed:opacity-0 data-enter:transform data-enter:duration-300 data-enter:ease-out data-closed:data-enter:translate-y-2 data-leave:duration-100 data-leave:ease-in data-closed:data-enter:sm:translate-x-2 data-closed:data-enter:sm:translate-y-0 dark:bg-gray-800 dark:-outline-offset-1 dark:outline-white/10 bg-yellow-50">
              <div className="p-4">
                <div className="flex items-start">
                  <div className="shrink-0">
                    <ExclamationTriangleIcon
                      aria-hidden="true"
                      className="size-5 text-yellow-400 dark:text-yellow-300"
                    />
                  </div>
                  <div className="ml-3 w-0 flex-1 pt-0.5">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Oops!
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {text}
                    </p>
                  </div>
                  <div className="ml-4 flex shrink-0">
                    <button
                      type="button"
                      onClick={close}
                      className="inline-flex rounded-md text-gray-400 hover:text-gray-500 focus:outline-2 focus:outline-offset-2 focus:outline-indigo-600 dark:hover:text-white dark:focus:outline-indigo-500"
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon aria-hidden="true" className="size-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </>
  );
}

export default ImageUpload;
